import { ALL_FORMATS, Input, UrlSource } from "mediabunny";
import type {
    ScriptLayerInputConfig,
    ScriptLayerSourceSegment,
} from "../../../engine/clips/script/script.interfaces";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { UndoManager } from "../../../librettoEditor/services/UndoManager";
import {
    DEFAULT_SPEAKER_COLORS,
} from "../../../librettoEditor/store/ScriptLayerStore";
import { isScriptSceneEmpty } from "../../../librettoEditor/utils/scriptSceneUtils";
import { maybeLockCanvasToFirstVideo } from "../../../utils/canvasSizing";
import {
    calculateVideoVisualDimensions,
    parseVideoResolution,
} from "./videoSizing";

export type ScriptSceneTrackType = "audio" | "video";

export type ResolveScriptSceneTrackUrl = (
    trackId: string,
    type: ScriptSceneTrackType,
) => Promise<string | undefined>;

export interface InsertTrackIntoScriptSceneArgs {
    trackId: string;
    trackType: ScriptSceneTrackType;
    /** ScriptClip-local/source-domain insertion point. */
    atClipLocalSec: number;
    resolveTrackUrl: ResolveScriptSceneTrackUrl;
    trackTitle?: string | null;
    trackResolution?: string | null;
    /**
     * Optional source window to insert. When omitted, the exact duration is
     * computed from the expected primary Mediabunny track during preflight.
     */
    durationSec?: number;
    /** Reuse an exactly matching empty scene at the anchor. Defaults to false. */
    preferExistingEmptyScene?: boolean;
    /** Abort if async media preflight completed against a different ScriptClip. */
    expectedScriptClipId?: string;
    /** Abort if the editor switched composition sessions during async preflight. */
    expectedCompositionSessionVersion?: number;
    /**
     * Optional caller-owned request guard. This keeps an insertion that was
     * canceled or superseded while media was loading from mutating the script.
     */
    isInsertionRequestCurrent?: () => boolean;
    /**
     * Optional pre-mutation target guard (for example a ScriptClip timing
     * fingerprint). It is intentionally not re-run after this insertion has
     * changed that timing model itself.
     */
    isInsertionTargetCurrent?: () => boolean;
    /**
     * Called while the mutation transaction is open. The editor uses this to
     * temporarily block competing pointer/keyboard edits during asynchronous
     * ScriptClip decoder setup, so a rollback cannot overwrite another edit.
     */
    onMutationPhaseChange?: (active: boolean) => void;
}

export interface InsertTrackIntoScriptSceneResult {
    scriptClipId: string;
    trackId: string;
    trackType: ScriptSceneTrackType;
    sceneIndex: number;
    sceneStartSec: number;
    sceneEndSec: number;
    timelineStartSec: number;
    timelineEndSec: number;
    durationSec: number;
    naturalDurationSec: number;
    appendedToExistingSource: boolean;
}

interface TrackPreflight {
    input: Input;
    durationSec: number;
    naturalDurationSec: number;
    videoWidth: number;
    videoHeight: number;
}

const EPS = 1e-3;
const MIN_SCENE_DURATION_SEC = 0.05;
const DURATION_TOLERANCE_SEC = 0.05;

type TimedRange = {
    id?: string;
    startSec: number;
    endSec: number;
    [key: string]: unknown;
};

const readTimedRanges = (value: unknown): TimedRange[] =>
    Array.isArray(value)
        ? value.filter((range: any) => {
              const startSec = Number(range?.startSec ?? NaN);
              const endSec = Number(range?.endSec ?? NaN);
              return Number.isFinite(startSec) && Number.isFinite(endSec) && endSec > startSec;
          })
        : [];

const snapshotStraddlingRanges = (value: unknown, atSec: number): TimedRange[] =>
    readTimedRanges(value)
        .filter(
            (range) =>
                Number(range.startSec) + EPS < atSec &&
                atSec < Number(range.endSec) - EPS,
        )
        .map((range) => ({ ...range }));

const timedRangeKey = (range: TimedRange, index: number): string =>
    String(range.id ?? `__index_${index}`);

/**
 * Temporarily keep only the left half of ranges that straddle the anchor. This
 * gives a point inside a collapsed cut an exact timeline join at which the
 * composition service can ripple downstream clips.
 */
const prepareRangesForInsertion = (
    currentRanges: unknown,
    straddlingRanges: TimedRange[],
    atSec: number,
): TimedRange[] => {
    if (!straddlingRanges.length) return readTimedRanges(currentRanges);
    const originalsById = new Map<string, TimedRange>(
        straddlingRanges.map((range, index) => [timedRangeKey(range, index), range]),
    );
    const nextRanges: TimedRange[] = [];

    const current = readTimedRanges(currentRanges);
    for (let index = 0; index < current.length; index += 1) {
        const range = current[index];
        const original = originalsById.get(timedRangeKey(range, index));
        if (!original) {
            nextRanges.push({ ...range });
            continue;
        }

        const leftStartSec = Number(original.startSec);
        if (atSec - leftStartSec > EPS) {
            nextRanges.push({ ...range, startSec: leftStartSec, endSec: atSec });
        }
    }
    return nextRanges;
};

/** Restore the old right-hand content while leaving the recording interval clear. */
const restoreRangesAfterInsertion = (
    currentRanges: unknown,
    straddlingRanges: TimedRange[],
    atSec: number,
    durationSec: number,
    idPrefix: "cut" | "gap",
): TimedRange[] => {
    const nextRanges = readTimedRanges(currentRanges).map((range) => ({ ...range }));
    for (const original of straddlingRanges) {
        const rightEndSec = Number(original.endSec) + durationSec;
        const rightStartSec = atSec + durationSec;
        if (rightEndSec - rightStartSec > EPS) {
            nextRanges.push({
                ...original,
                id:
                    globalThis.crypto?.randomUUID?.() ??
                    `${idPrefix}_recording_${Date.now()}_${Math.random()}`,
                startSec: rightStartSec,
                endSec: rightEndSec,
            });
        }
    }
    return nextRanges;
};

const applyTimedRangeEdits = (
    scriptClip: any,
    scriptCuts: TimedRange[],
    gaps: TimedRange[],
): void => {
    if (typeof scriptClip?.applyPersistedEdits === "function") {
        scriptClip.applyPersistedEdits({ scriptCuts, gaps });
    } else {
        scriptClip.scriptCuts = scriptCuts;
        scriptClip.gaps = gaps;
    }
};

const readScenes = (scriptClip: any): any[] =>
    Array.isArray(scriptClip?.getScenes?.()) ? scriptClip.getScenes() : [];

const findSceneByStart = (scriptClip: any, startSec: number): any | null =>
    readScenes(scriptClip).find(
        (scene: any) => Math.abs(Number(scene?.startSec ?? NaN) - startSec) <= EPS,
    ) ?? null;

const findReusableEmptyScene = (scriptClip: any, atSec: number): any | null => {
    const scenes = readScenes(scriptClip);
    const scene =
        scenes.find((candidate: any) => {
            const startSec = Number(candidate?.startSec ?? NaN);
            const endSec = Number(candidate?.endSec ?? NaN);
            return (
                Number.isFinite(startSec) &&
                Number.isFinite(endSec) &&
                atSec >= startSec - EPS &&
                atSec < endSec - EPS
            );
        }) ??
        scenes.find(
            (candidate: any) =>
                Math.abs(Number(candidate?.startSec ?? NaN) - atSec) <= EPS,
        ) ??
        null;

    return scene && isScriptSceneEmpty(scriptClip, scene) ? scene : null;
};

const hasStableSourcePlacements = (scriptClip: any): boolean => {
    const configs: any[] = Array.isArray(scriptClip?.getInputConfigs?.())
        ? scriptClip.getInputConfigs()
        : [];
    return configs.every((config: any) => {
        const segments: any[] = Array.isArray(config?.segments) ? config.segments : [];
        if (segments.length > 0) {
            return segments.every((segment: any) => {
                const offsetSec = Number(segment?.offsetSec ?? NaN);
                const inSec = Number(segment?.range?.inSec ?? NaN);
                const outSec = Number(segment?.range?.outSec ?? NaN);
                return (
                    Number.isFinite(offsetSec) &&
                    Number.isFinite(inSec) &&
                    Number.isFinite(outSec) &&
                    outSec > inSec + EPS
                );
            });
        }

        const inSec = Number(config?.range?.inSec ?? NaN);
        const outSec = Number(config?.range?.outSec ?? NaN);
        return Number.isFinite(inSec) && Number.isFinite(outSec) && outSec > inSec + EPS;
    });
};

/**
 * Resolve and probe the media before any editor state is mutated.
 */
async function preflightTrack(
    args: InsertTrackIntoScriptSceneArgs,
): Promise<TrackPreflight | null> {
    const trackId = String(args.trackId ?? "").trim();
    if (!trackId) return null;

    let url: string | undefined;
    try {
        url = await args.resolveTrackUrl(trackId, args.trackType);
    } catch {
        return null;
    }
    if (!url) return null;

    let input: Input;
    try {
        input = new Input({
            source: new UrlSource(url),
            formats: ALL_FORMATS,
        });
    } catch {
        return null;
    }

    let primaryTrack: any = null;
    try {
        primaryTrack =
            args.trackType === "video"
                ? await input.getPrimaryVideoTrack()
                : await input.getPrimaryAudioTrack();
    } catch {
        return null;
    }
    if (!primaryTrack || typeof primaryTrack.canDecode !== "function") return null;

    try {
        if (!(await primaryTrack.canDecode())) return null;
    } catch {
        return null;
    }

    let naturalDurationSec = 0;
    try {
        naturalDurationSec = Number(await primaryTrack.computeDuration());
    } catch {
        return null;
    }
    if (!Number.isFinite(naturalDurationSec) || naturalDurationSec < MIN_SCENE_DURATION_SEC) {
        return null;
    }

    const requestedDuration = Number(args.durationSec ?? NaN);
    const hasRequestedDuration = args.durationSec != null;
    if (
        hasRequestedDuration &&
        (!Number.isFinite(requestedDuration) || requestedDuration < MIN_SCENE_DURATION_SEC)
    ) {
        return null;
    }
    if (
        hasRequestedDuration &&
        requestedDuration > naturalDurationSec + DURATION_TOLERANCE_SEC
    ) {
        // Do not create a placement that reads beyond the finalized media.
        return null;
    }

    const durationSec = hasRequestedDuration ? requestedDuration : naturalDurationSec;
    let videoWidth = 0;
    let videoHeight = 0;
    if (args.trackType === "video") {
        videoWidth = Number(primaryTrack.displayWidth ?? primaryTrack.codedWidth ?? 0);
        videoHeight = Number(primaryTrack.displayHeight ?? primaryTrack.codedHeight ?? 0);
        if (!(videoWidth > 0 && videoHeight > 0)) {
            const parsed = parseVideoResolution(args.trackResolution);
            videoWidth = parsed.width;
            videoHeight = parsed.height;
        }
    }

    return {
        input,
        durationSec,
        naturalDurationSec,
        videoWidth,
        videoHeight,
    };
}

/**
 * Atomically place an audio/video asset into a ScriptClip scene.
 *
 * All URL, decode and duration checks complete before the first composition
 * mutation. Structural scene edits, downstream ripple edits and source
 * attachment are grouped into one editor undo transaction.
 */
export async function insertTrackIntoScriptScene(
    args: InsertTrackIntoScriptSceneArgs,
): Promise<InsertTrackIntoScriptSceneResult | null> {
    const trackId = String(args.trackId ?? "").trim();
    const atClipLocalSec = Number(args.atClipLocalSec ?? NaN);
    if (!trackId || !Number.isFinite(atClipLocalSec) || atClipLocalSec < 0) return null;

    const preflight = await preflightTrack(args);
    if (!preflight) return null;

    let composition: any = null;
    let scriptClip: any = null;
    try {
        composition = CompositionService.composition;
        scriptClip = CompositionService.getScriptLayerClip?.();
    } catch {
        return null;
    }
    if (!composition || !scriptClip || String(scriptClip?.type ?? "") !== "script") {
        return null;
    }
    if (!hasStableSourcePlacements(scriptClip)) return null;

    const clipInSec = Number(scriptClip?.range?.[0]?.seconds ?? 0);
    const clipOutSec = Number(
        scriptClip?.range?.[1]?.seconds ?? scriptClip?.duration?.seconds ?? clipInSec,
    );
    if (!Number.isFinite(clipInSec) || !Number.isFinite(clipOutSec) || clipOutSec < clipInSec) {
        return null;
    }
    const insertionAtSec = Math.max(clipInSec, Math.min(clipOutSec, atClipLocalSec));
    const straddlingCutsBeforeInsert = snapshotStraddlingRanges(
        scriptClip?.scriptCuts,
        insertionAtSec,
    );
    const straddlingGapsBeforeInsert = snapshotStraddlingRanges(
        scriptClip?.gaps,
        insertionAtSec,
    );
    const preferExisting = args.preferExistingEmptyScene === true;
    const reusableCandidate = preferExisting
        ? findReusableEmptyScene(scriptClip, insertionAtSec)
        : null;
    // There is no safe generic "shrink empty scene" primitive: cutting a tail
    // removes its boundary and can merge the recording with the next scene.
    // Reuse only when no timing surgery is required. Otherwise insert a fresh,
    // exact-duration scene at the user's actual anchor.
    const reusableCandidateStartSec = Number(reusableCandidate?.startSec ?? NaN);
    const reusableCandidateDurationSec =
        Number(reusableCandidate?.endSec ?? NaN) - reusableCandidateStartSec;
    const reusableScene =
        reusableCandidate &&
        Number.isFinite(reusableCandidateStartSec) &&
        Number.isFinite(reusableCandidateDurationSec) &&
        Math.abs(reusableCandidateStartSec - insertionAtSec) <= EPS &&
        Math.abs(reusableCandidateDurationSec - preflight.durationSec) <= EPS
            ? reusableCandidate
            : null;

    // These guards intentionally run after the asynchronous media probe and as the
    // final check before opening the transaction/mutating composition state.
    if (
        args.expectedScriptClipId != null &&
        String(scriptClip.id) !== String(args.expectedScriptClipId)
    ) {
        return null;
    }
    if (args.expectedCompositionSessionVersion != null) {
        const currentSessionVersion = Number(CompositionService.getSessionVersion?.());
        if (
            !Number.isFinite(currentSessionVersion) ||
            currentSessionVersion !== Number(args.expectedCompositionSessionVersion)
        ) {
            return null;
        }
    }
    if (args.isInsertionTargetCurrent) {
        try {
            if (!args.isInsertionTargetCurrent()) return null;
        } catch {
            return null;
        }
    }
    if (args.isInsertionRequestCurrent) {
        try {
            if (!args.isInsertionRequestCurrent()) return null;
        } catch {
            return null;
        }
    }

    let result: InsertTrackIntoScriptSceneResult | null = null;
    let transactionClosed = false;
    let transactionStarted = false;
    let scriptBatchStarted = false;
    let mutationPhaseActive = false;

    try {
        args.onMutationPhaseChange?.(true);
        mutationPhaseActive = true;
        UndoManager.beginTransaction();
        transactionStarted = true;
        CompositionService.beginScriptLayerBatch();
        scriptBatchStarted = true;

        if (
            args.trackType === "video" &&
            preflight.videoWidth > 0 &&
            preflight.videoHeight > 0
        ) {
            maybeLockCanvasToFirstVideo(
                composition,
                preflight.videoWidth,
                preflight.videoHeight,
            );
        }

        let scene = reusableScene;
        let insertedFreshScene = false;
        let sceneStartSec = Number(scene?.startSec ?? insertionAtSec);
        if (!scene) {
            // A ScriptClip cut collapses its source interval out of composition
            // time. Temporarily keep its left half only so the insertion anchor
            // becomes the exact visible join; the right half is restored after
            // the recorded segment with its downstream offset applied.
            if (
                straddlingCutsBeforeInsert.length > 0 ||
                straddlingGapsBeforeInsert.length > 0
            ) {
                applyTimedRangeEdits(
                    scriptClip,
                    prepareRangesForInsertion(
                        scriptClip?.scriptCuts,
                        straddlingCutsBeforeInsert,
                        insertionAtSec,
                    ),
                    prepareRangesForInsertion(
                        scriptClip?.gaps,
                        straddlingGapsBeforeInsert,
                        insertionAtSec,
                    ),
                );
            }
            const inserted = CompositionService.insertEmptyScriptScene(
                insertionAtSec,
                preflight.durationSec,
            );
            if (!inserted) throw new Error("Unable to insert an empty ScriptClip scene");
            insertedFreshScene = true;
            sceneStartSec = insertionAtSec;
            scene = findSceneByStart(scriptClip, sceneStartSec);
        }

        if (!scene || !isScriptSceneEmpty(scriptClip, scene)) {
            throw new Error("The target ScriptClip scene is not empty");
        }

        const currentDurationSec =
            Number(scene?.endSec ?? 0) - Number(scene?.startSec ?? 0);
        if (!Number.isFinite(currentDurationSec) || currentDurationSec <= EPS) {
            throw new Error("The target ScriptClip scene has invalid bounds");
        }

        if (Math.abs(currentDurationSec - preflight.durationSec) > EPS) {
            throw new Error("The target ScriptClip scene does not match the recording duration");
        }

        if (!scene) throw new Error("Unable to resolve the inserted ScriptClip scene");

        const segment: ScriptLayerSourceSegment = {
            id:
                globalThis.crypto?.randomUUID?.() ??
                `seg_${Date.now()}_${Math.random()}`,
            offsetSec: sceneStartSec,
            range: { inSec: 0, outSec: preflight.durationSec },
        };

        const existingConfigs: any[] = Array.isArray(scriptClip.getInputConfigs?.())
            ? scriptClip.getInputConfigs()
            : [];
        const existingConfig = existingConfigs.find(
            (config: any) => String(config?.assetId ?? "").trim() === trackId,
        );
        const appendedToExistingSource = Boolean(existingConfig);

        if (existingConfig) {
            if (!scriptClip.appendSourceSegments?.(trackId, [segment])) {
                throw new Error("Unable to append the ScriptClip source segment");
            }
        } else {
            const sourceColor =
                DEFAULT_SPEAKER_COLORS[
                    existingConfigs.length % DEFAULT_SPEAKER_COLORS.length
                ];
            const config: ScriptLayerInputConfig = {
                input: preflight.input,
                assetId: trackId,
                name: String(args.trackTitle ?? "").trim(),
                color: sourceColor,
                gain: 1,
                segments: [segment],
            };

            if (args.trackType === "video") {
                const compWidth = Number(composition?.width ?? 1920) || 1920;
                const compHeight = Number(composition?.height ?? 1080) || 1080;
                const visual = calculateVideoVisualDimensions(
                    preflight.videoWidth,
                    preflight.videoHeight,
                    compWidth,
                    compHeight,
                );
                config.visual = {
                    x: visual.x,
                    y: visual.y,
                    width: visual.width,
                    height: visual.height,
                    alpha: 1,
                };
            }

            await scriptClip.appendInputConfig?.(config);

            const appendedConfig = (scriptClip.getInputConfigs?.() ?? []).find(
                (candidate: any) => candidate === config,
            );
            const appendedRuntimeEntry =
                args.trackType === "video"
                    ? (scriptClip.getVideoEntries?.() ?? []).some(
                          (entry: any) => entry?.input === preflight.input,
                      )
                    : (scriptClip.getAudioEntries?.() ?? []).some(
                          (entry: any) => entry?.input === preflight.input,
                      );
            if (!appendedConfig || !appendedRuntimeEntry) {
                scriptClip.removeLastInputConfigByAssetId?.(trackId);
                throw new Error("The ScriptClip source could not be initialized");
            }
        }

        if (
            insertedFreshScene &&
            (straddlingCutsBeforeInsert.length > 0 ||
                straddlingGapsBeforeInsert.length > 0)
        ) {
            applyTimedRangeEdits(
                scriptClip,
                restoreRangesAfterInsertion(
                    scriptClip?.scriptCuts,
                    straddlingCutsBeforeInsert,
                    insertionAtSec,
                    preflight.durationSec,
                    "cut",
                ),
                restoreRangesAfterInsertion(
                    scriptClip?.gaps,
                    straddlingGapsBeforeInsert,
                    insertionAtSec,
                    preflight.durationSec,
                    "gap",
                ),
            );
        }

        // appendInputConfig performs asynchronous decoder setup. Re-check the target
        // before broadcasting/persisting in case the editor switched underneath it.
        let targetStillCurrent = false;
        try {
            targetStillCurrent =
                CompositionService.composition === composition &&
                CompositionService.getScriptLayerClip?.() === scriptClip;
            if (
                targetStillCurrent &&
                args.expectedCompositionSessionVersion != null
            ) {
                targetStillCurrent =
                    Number(CompositionService.getSessionVersion?.()) ===
                    Number(args.expectedCompositionSessionVersion);
            }
            if (targetStillCurrent && args.isInsertionRequestCurrent) {
                targetStillCurrent = args.isInsertionRequestCurrent();
            }
        } catch {
            targetStillCurrent = false;
        }
        if (!targetStillCurrent) {
            throw new Error("The active composition changed during ScriptClip insertion");
        }

        CompositionService.notifyScriptLayerUpdated({}, { timingChanged: true });
        CompositionService.notifyClipUpdated(scriptClip, { timingChanged: false });
        try {
            composition.computeFrame?.();
        } catch {}

        const resolvedScene = findSceneByStart(scriptClip, sceneStartSec) ?? scene;
        const resolvedStartSec = Number(resolvedScene?.startSec ?? sceneStartSec);
        const resolvedEndSec = Number(resolvedScene?.endSec ?? NaN);
        const resolvedTimelineStartSec = Number(resolvedScene?.timelineStartSec ?? 0);
        const resolvedTimelineEndSec = Number(resolvedScene?.timelineEndSec ?? NaN);
        result = {
            scriptClipId: String(scriptClip.id),
            trackId,
            trackType: args.trackType,
            sceneIndex: Number(resolvedScene?.index ?? 0),
            sceneStartSec: resolvedStartSec,
            sceneEndSec: Number.isFinite(resolvedEndSec)
                ? resolvedEndSec
                : resolvedStartSec + preflight.durationSec,
            timelineStartSec: resolvedTimelineStartSec,
            timelineEndSec: Number.isFinite(resolvedTimelineEndSec)
                ? resolvedTimelineEndSec
                : resolvedTimelineStartSec + preflight.durationSec,
            durationSec: preflight.durationSec,
            naturalDurationSec: preflight.naturalDurationSec,
            appendedToExistingSource,
        };
    } catch (error) {
        console.warn("[insertTrackIntoScriptScene] Insertion failed", error);
        result = null;
        if (transactionStarted) {
            try {
                await UndoManager.abortTransaction();
                transactionClosed = true;
            } catch (rollbackError) {
                console.error(
                    "[insertTrackIntoScriptScene] Failed to roll back insertion",
                    rollbackError,
                );
            }
        }
    } finally {
        try {
            if (scriptBatchStarted) {
                await CompositionService.endScriptLayerBatch();
            }
        } finally {
            if (transactionStarted && !transactionClosed) UndoManager.endTransaction();
            if (mutationPhaseActive) {
                try {
                    args.onMutationPhaseChange?.(false);
                } catch {}
            }
        }
    }

    return result;
}
